import type { UploadProgressPresentationStage } from '@sosocio/creator-types/app';

/**
 * Resolves the compatibility stage for callers that still provide only a percentage.
 *
 * @param stage Explicit lifecycle stage, when available.
 * @param progressPercentage Legacy aggregate progress percentage.
 * @returns Authoritative or compatibility-projected presentation stage.
 */
export function resolveUploadProgressStage(
	stage: UploadProgressPresentationStage | undefined,
	progressPercentage: number,
): UploadProgressPresentationStage {
	if (stage) {
		return stage;
	}

	if (progressPercentage >= 100) {
		return 'completed';
	}

	return progressPercentage <= 0 ? 'selected' : 'transferring';
}

/**
 * Determines whether the final photo actions may replace in-flight progress.
 *
 * @param stage Resolved lifecycle presentation stage.
 * @returns Whether the batch is explicitly completed.
 */
export function isUploadProgressComplete(stage: UploadProgressPresentationStage): boolean {
	return stage === 'completed';
}

/**
 * Determines whether remaining work is not meaningfully measurable as a percentage.
 *
 * @param stage Resolved lifecycle presentation stage.
 * @returns Whether the view should show a spinner and named status without a bar.
 */
export function isUploadProgressIndeterminate(stage: UploadProgressPresentationStage): boolean {
	return stage === 'processing' || stage === 'saving' || stage === 'reconnecting';
}

/**
 * Selects the progress-status translation for the current lifecycle stage.
 *
 * @param stage Resolved lifecycle presentation stage.
 * @param progressPercentage Legacy percentage used during selected/transferring.
 * @returns Translation key rendered beneath the spinner/progress bar.
 */
export function getUploadProgressTextKey(
	stage: UploadProgressPresentationStage,
	progressPercentage: number,
): string {
	if (stage === 'processing') {
		return 'views.uploadProgress.processing';
	}

	if (stage === 'saving') {
		return 'views.uploadProgress.saving';
	}

	if (stage === 'reconnecting') {
		return 'views.uploadProgress.reconnecting';
	}

	return progressPercentage >= 5
		? 'views.uploadProgress.uploading'
		: 'views.uploadProgress.upload_starting';
}
